import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { fetchExchangeRate } from "../utils/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import PrintQRCode from "./ui/PrintQRCode";
import SummaryTab from "./tabs/SummaryTab";
import FlightTab from "./tabs/FlightTab";
import AccommodationTab from "./tabs/AccommodationTab";
import InsuranceTab from "./tabs/InsuranceTab";
import ShoppingTab from "./tabs/ShoppingTab";
import MembersTab from "./tabs/MembersTab";
import ContactsTab from "./tabs/ContactsTab";
import ExpenseTab from "./tabs/ExpenseTab";

const TripDetail = ({ trip, onBack, isDarkMode, theme }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [printLang, setPrintLang] = useState("th");
  const [updateTrigger, setUpdateTrigger] = useState(0);

  const [formType, setFormType] = useState("place");
  const [activityName, setActivityName] = useState("");
  const [activityPlace, setActivityPlace] = useState("");
  const [googleMapUrl, setGoogleMapUrl] = useState("");
  const [otherUrl, setOtherUrl] = useState("");
  const [activityDetail, setActivityDetail] = useState("");
  const [cost, setCost] = useState("");
  const [currency, setCurrency] = useState("THB");
  const [category, setCategory] = useState("food");

  // State สำหรับจัดการหมวดหมู่ที่เพิ่มเข้ามาใหม่
  const [customCategories, setCustomCategories] = useState(
    trip.customCategories || []
  );
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [todoTitle, setTodoTitle] = useState("");
  const [tasksText, setTasksText] = useState("");

  const [activityDate, setActivityDate] = useState(trip.start);
  const [activityTime, setActivityTime] = useState("09:00");

  const [exchangeRate, setExchangeRate] = useState(0.23);
  const [editingId, setEditingId] = useState(null);
  const [activeMapQuery, setActiveMapQuery] = useState("Japan");
  const [editingCityFlightId, setEditingCityFlightId] = useState(null);
  const [editCityDep, setEditCityDep] = useState("");
  const [editCityArr, setEditCityArr] = useState("");
  const [inlineAddDate, setInlineAddDate] = useState(null);

  const detailTextareaRef = useRef(null);
  const todoTextareaRef = useRef(null);

  const d = isDarkMode;

  useEffect(() => {
    if (detailTextareaRef.current) {
      detailTextareaRef.current.style.height = "auto";
      detailTextareaRef.current.style.height = `${
        detailTextareaRef.current.scrollHeight + 2
      }px`;
    }
  }, [activityDetail, formType, editingId, inlineAddDate]);

  useEffect(() => {
    if (todoTextareaRef.current) {
      todoTextareaRef.current.style.height = "auto";
      todoTextareaRef.current.style.height = `${
        todoTextareaRef.current.scrollHeight + 2
      }px`;
    }
  }, [tasksText, formType, editingId, inlineAddDate]);

  // รวมหมวดหมู่เริ่มต้นและหมวดหมู่ที่ผู้ใช้สร้างเอง
  const defaultCategories = [
    { id: "travel", label: "เดินทาง", icon: "🚕" },
    { id: "food", label: "อาหาร", icon: "🍜" },
    { id: "souvenir", label: "ของฝาก", icon: "🛍️" },
    { id: "other", label: "อื่นๆ", icon: "📍" },
  ];
  const categoryOptions = [...defaultCategories, ...customCategories];

  const tabs = [
    { id: "summary", label: "สรุป", icon: "📊", color: "#f59e0b" },
    { id: "plan", label: "แผนเที่ยว", icon: "📝", color: "#3b82f6" },
    { id: "flight", label: "ไฟล์ท", icon: "✈️", color: "#10b981" },
    { id: "stay", label: "ที่พัก", icon: "🏨", color: "#0ea5e9" },
    { id: "shopping", label: "เตรียมของ", icon: "🛍️", color: "#f43f5e" },
    { id: "expense", label: "บัญชีทริป", icon: "💸", color: "#2ed573" },
    { id: "insurance", label: "ประกัน", icon: "🛡️", color: "#8b5cf6" },
    { id: "members", label: "ผู้เดินทาง", icon: "👥", color: "#a855f7" },
    { id: "contacts", label: "ติดต่อฉุกเฉิน", icon: "📞", color: "#e74c3c" },
  ];

  // ─── i18n ────────────────────────────────────────────────────────
  const i18n = {
    th: {
      docTitle: "แผนการเดินทาง",
      period: "วันที่เดินทาง",
      to: "ถึง",
      itinerarySection: "รายละเอียดแผนการเดินทาง (Itinerary)",
      dateTitle: "วันที่",
      flightSection: "รายการเที่ยวบิน",
      staySection: "ข้อมูลที่พัก",
      insuranceSection: "ข้อมูลประกันเดินทาง",
      membersSection: "รายชื่อผู้เดินทาง",
      colDate: "วันที่",
      colFlight: "ไฟล์ท",
      colRoute: "เส้นทาง",
      colTime: "เวลา",
      colHotel: "ที่พัก",
      colPrice: "ราคา",
      colTotal: "ราคารวม",
      colCheckInOut: "เช็คอิน/เอาท์",
      colInsName: "บริษัท / แผน",
      colPolicy: "เลขกรมธรรม์",
      colContact: "เบอร์ฉุกเฉิน",
      contactsSection: "เบอร์ติดต่อและข้อมูลฉุกเฉิน",
      colContactTitle: "ชื่อหน่วยงาน / บุคคล",
      colContactInfo: "ข้อมูลติดต่อ",
    },
    en: {
      docTitle: "Travel Itinerary",
      period: "Travel Period",
      to: "to",
      itinerarySection: "Daily Itinerary",
      dateTitle: "Date",
      flightSection: "Flight Schedule",
      staySection: "Accommodation Details",
      insuranceSection: "Travel Insurance",
      membersSection: "Travelers Information",
      colDate: "Date",
      colFlight: "Flight",
      colRoute: "Route",
      colTime: "Time",
      colHotel: "Hotel",
      colPrice: "Price",
      colTotal: "Total Price",
      colCheckInOut: "Check-in/out",
      colInsName: "Company / Plan",
      colPolicy: "Policy No.",
      colContact: "Emergency Contact",
      contactsSection: "Emergency Contacts & Info",
      colContactTitle: "Name / Organization",
      colContactInfo: "Contact Details",
    },
  };
  const t = i18n[printLang];

  useEffect(() => {
    fetchExchangeRate().then(setExchangeRate);
    setActiveMapQuery(trip.name);
  }, [trip.name]);

  const inputStyle = {
    padding: "9px 12px",
    borderRadius: "10px",
    border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
    backgroundColor: d ? "#09090b" : "#f4f4f5",
    color: d ? "#fafafa" : "#09090b",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  };
  const labelStyle = {
    fontSize: "11px",
    fontWeight: "600",
    color: d ? "#71717a" : "#a1a1aa",
    marginBottom: "4px",
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };
  const formBgStyle = {
    backgroundColor: d ? "#18181b" : "#fff",
    borderRadius: "14px",
    padding: "20px",
    border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
    boxShadow: d ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.06)",
    marginBottom: "12px",
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(trip, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Trip-${trip.name}.json`;
    a.click();
  };

  const handleActivityTimeInput = (e) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length >= 3) val = val.slice(0, 2) + ":" + val.slice(2, 4);
    setActivityTime(val);
  };

  const handleOpenAddForm = (targetDate = null) => {
    setFormType("place");
    setActivityName("");
    setActivityPlace("");
    setGoogleMapUrl("");
    setOtherUrl("");
    setActivityDetail("");
    setCost("");
    setTodoTitle("");
    setTasksText("");
    setActivityDate(targetDate || trip.start || "");
    setActivityTime("09:00");
    setEditingId(null);
    setInlineAddDate(targetDate || "TOP");
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setIsAddingCategory(false);
      return;
    }

    const newCat = {
      id: `custom_${Date.now()}`,
      label: newCategoryName.trim(),
      icon: "🏷️",
    };

    const updatedCategories = [...customCategories, newCat];

    try {
      await updateDoc(doc(db, "trips", trip.id), {
        customCategories: updatedCategories,
      });

      trip.customCategories = updatedCategories;
      setCustomCategories(updatedCategories);
      setCategory(newCat.id);

      setNewCategoryName("");
      setIsAddingCategory(false);
    } catch (error) {
      console.error("Error saving custom category:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกหมวดหมู่");
    }
  };

  const handleSaveActivity = async (e) => {
    e.preventDefault();
    const finalTime = activityTime || "09:00";
    let activityData = {};
    if (formType === "place") {
      if (!activityName) return alert("กรอกชื่อสถานที่ด้วยครับ");
      const finalCostTHB =
        currency === "JPY"
          ? parseFloat(cost || 0) * exchangeRate
          : parseFloat(cost || 0);
      activityData = {
        id: editingId || Date.now(),
        type: "place",
        name: activityName,
        place: activityPlace,
        googleMapUrl,
        otherUrl,
        detail: activityDetail,
        date: activityDate,
        time: finalTime,
        inputCost: parseFloat(cost || 0),
        currency,
        costTHB: finalCostTHB,
        categoryId: category,
        order: editingId
          ? trip.itinerary.find((i) => i.id === editingId)?.order
          : Date.now(),
      };
    } else {
      if (!todoTitle) return alert("กรอกหัวข้อ Todo ด้วยครับ");
      const existingTasks = editingId
        ? trip.itinerary.find((i) => i.id === editingId)?.tasks || []
        : [];
      const taskArray = tasksText
        .split("\n")
        .filter((t) => t.trim() !== "")
        .map((text, idx) => {
          const existing = existingTasks.find((et) => et.text === text.trim());
          return {
            id: existing ? existing.id : `task_${Date.now()}_${idx}`,
            text: text.trim(),
            isCompleted: existing ? existing.isCompleted : false,
          };
        });
      activityData = {
        id: editingId || Date.now(),
        type: "todo",
        name: todoTitle,
        date: activityDate,
        time: finalTime,
        tasks: taskArray,
        order: editingId
          ? trip.itinerary.find((i) => i.id === editingId)?.order
          : Date.now(),
      };
    }
    try {
      const newItinerary = editingId
        ? trip.itinerary.map((item) =>
            item.id === editingId ? activityData : item
          )
        : [...(trip.itinerary || []), activityData];
      await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary });
      trip.itinerary = newItinerary;
      if (formType === "place")
        setActiveMapQuery(activityPlace || activityName);
      setActivityName("");
      setActivityPlace("");
      setGoogleMapUrl("");
      setOtherUrl("");
      setActivityDetail("");
      setCost("");
      setTodoTitle("");
      setTasksText("");
      setEditingId(null);
      setInlineAddDate(null);
    } catch {
      alert("Error saving");
    }
  };

  const handleToggleTodo = async (activityId, taskId) => {
    const updated = trip.itinerary.map((item) => {
      if (item.id === activityId && item.type === "todo") {
        return {
          ...item,
          tasks: item.tasks.map((task) =>
            task.id === taskId
              ? { ...task, isCompleted: !task.isCompleted }
              : task
          ),
        };
      }
      return item;
    });
    await updateDoc(doc(db, "trips", trip.id), { itinerary: updated });
    trip.itinerary = updated;
    setUpdateTrigger((p) => p + 1);
  };

  const handleDeleteActivity = async (id) => {
    if (window.confirm("ลบรายการนี้ใช่ไหม?")) {
      const nl = trip.itinerary.filter((i) => i.id !== id);
      await updateDoc(doc(db, "trips", trip.id), { itinerary: nl });
      trip.itinerary = nl;
      setUpdateTrigger((p) => p + 1);
      setEditingId(null);
      setInlineAddDate(null);
    }
  };

  const handleUpdateTripData = (newData, fieldName) => {
    trip[fieldName] = newData;
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setInlineAddDate(null);
    setFormType(item.type || "place");
    if (item.type === "todo") {
      setTodoTitle(item.name);
      setTasksText(item.tasks?.map((t) => t.text).join("\n") || "");
    } else {
      setActivityName(item.name);
      setActivityPlace(item.place || "");
      setGoogleMapUrl(item.googleMapUrl || "");
      setOtherUrl(item.otherUrl || "");
      setActivityDetail(item.detail || "");
      setCost(item.inputCost || "");
      setCurrency(item.currency || "THB");
      setCategory(item.categoryId || "food");
    }
    setActivityDate(item.date);
    setActivityTime(item.time || "09:00");
  };

  const handleSaveFlightCity = async (flightId) => {
    try {
      const updatedFlights = (trip.flightList || []).map((f) =>
        f.id === flightId
          ? { ...f, depCity: editCityDep, arrCity: editCityArr }
          : f
      );
      await updateDoc(doc(db, "trips", trip.id), {
        flightList: updatedFlights,
      });
      trip.flightList = updatedFlights;
      setUpdateTrigger((p) => p + 1);
      setEditingCityFlightId(null);
    } catch {
      alert("Error saving city");
    }
  };

  const getCheckInTime = (timeStr) => {
    if (!timeStr || !timeStr.includes(":")) return null;
    const [hStr, mStr] = timeStr.split(":");
    let h = parseInt(hStr, 10),
      m = parseInt(mStr, 10);
    if (isNaN(h) || isNaN(m)) return null;
    h -= 3;
    let note = "";
    if (h < 0) {
      h += 24;
      note = " (ของคืนก่อนหน้า)";
    }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}${note}`;
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (String(draggableId).startsWith("v_")) return;
    const sourceDate = source.droppableId,
      destDate = destination.droppableId;
    let newItinerary = [...(trip.itinerary || [])];
    const draggedItemIndex = newItinerary.findIndex(
      (i) => String(i.id) === draggableId
    );
    if (draggedItemIndex === -1) return;
    const draggedItem = newItinerary[draggedItemIndex];
    if (sourceDate !== destDate) draggedItem.date = destDate;
    let destList = groupedItinerary[destDate]
      ? [...groupedItinerary[destDate]]
      : [];
    destList = destList.filter((i) => String(i.id) !== draggableId);
    destList.splice(destination.index, 0, draggedItem);
    let currentOrder = 0;
    destList.forEach((item) => {
      if (!item.isVirtual) {
        const target = newItinerary.find((i) => i.id === item.id);
        if (target) target.order = currentOrder++;
      }
    });
    if (sourceDate !== destDate) {
      let sourceList = groupedItinerary[sourceDate]
        ? [...groupedItinerary[sourceDate]]
        : [];
      sourceList = sourceList.filter((i) => String(i.id) !== draggableId);
      let srcOrder = 0;
      sourceList.forEach((item) => {
        if (!item.isVirtual) {
          const target = newItinerary.find((i) => i.id === item.id);
          if (target) target.order = srcOrder++;
        }
      });
    }
    await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary });
    trip.itinerary = newItinerary;
    setUpdateTrigger((p) => p + 1);
  };

  const rawItinerary = [...(trip.itinerary || [])];
  const virtualItems = [];

  (trip.flightList || []).forEach((f) => {
    const depDate = f.depDate || f.date,
      arrDate = f.arrDate || f.date;
    const fNo = f.flightNo || "",
      airlineName = f.airline || "";
    const timeDep = f.depTime || f.time || "00:00";
    const recommendedCheckIn = getCheckInTime(timeDep);
    if (depDate) {
      virtualItems.push({
        id: `v_dep_${f.id}`,
        originalId: f.id,
        isVirtual: true,
        type: "flight",
        sourceTab: "flight",
        name: `🛫 ออกเดินทาง (${f.depCode || "-"} ➔ ${f.arrCode || "-"})`,
        detail: `สายการบิน ${airlineName} ${fNo}\nเมืองต้นทาง: ${
          f.depCity || "ไม่ระบุ"
        } | ปลายทาง: ${f.arrCity || "ไม่ระบุ"}`,
        date: depDate,
        time: timeDep,
        otherUrl: f.ticketLink,
        checkInTime: recommendedCheckIn,
        mapQuery: `${f.depCode || f.depCity || ""} Airport`.trim(),
      });
    }
    if (
      arrDate &&
      (arrDate !== depDate || (f.arrTime && f.arrTime !== f.depTime))
    ) {
      virtualItems.push({
        id: `v_arr_${f.id}`,
        originalId: f.id,
        isVirtual: true,
        type: "flight",
        sourceTab: "flight",
        name: `🛬 เดินทางถึง (${f.arrCode || "-"})`,
        detail: `เมืองต้นทาง: ${f.depCity || "ไม่ระบุ"} | ปลายทาง: ${
          f.arrCity || "ไม่ระบุ"
        }`,
        date: arrDate,
        time: f.arrTime || "00:00",
        otherUrl: f.ticketLink,
        mapQuery: `${f.arrCode || f.arrCity || ""} Airport`.trim(),
      });
    }
  });

  (trip.accommodations || []).forEach((a) => {
    if (a.checkInDate) {
      let det = [];
      if (a.bookingRef) det.push(`หมายเลขการจอง: ${a.bookingRef}`);
      if (a.accessCode) det.push(`🔑 รหัสเข้าพัก: ${a.accessCode}`);
      if (a.agency) det.push(`🏢 จองผ่าน: ${a.agency}`);
      if (a.phone) det.push(`📞 โทร: ${a.phone}`);
      if (a.email) det.push(`📧 อีเมล: ${a.email}`);
      if (det.length === 0) det.push("เช็คอินเข้าที่พัก");

      virtualItems.push({
        id: `v_cin_${a.id}`,
        originalId: a.id,
        isVirtual: true,
        type: "stay",
        sourceTab: "stay",
        name: `🏨 Check-in: ${a.name}`,
        detail: det.join("\n"),
        date: a.checkInDate,
        time: a.checkInTime || "14:00",
        googleMapUrl: a.googleMap,
        otherUrl: a.link,
        guideLink: a.guideLink,
        mapQuery: a.name,
      });
    }
    if (a.checkOutDate)
      virtualItems.push({
        id: `v_cout_${a.id}`,
        originalId: a.id,
        isVirtual: true,
        type: "stay",
        sourceTab: "stay",
        name: `🛎️ Check-out: ${a.name}`,
        detail: "คืนห้องพัก",
        date: a.checkOutDate,
        time: a.checkOutTime || "11:00",
        mapQuery: a.name,
      });
  });

  const groupedItinerary = {};
  rawItinerary.forEach((item) => {
    if (!groupedItinerary[item.date]) groupedItinerary[item.date] = [];
    groupedItinerary[item.date].push(item);
  });
  const allDatesSet = new Set([
    ...Object.keys(groupedItinerary),
    ...virtualItems.map((v) => v.date),
  ]);
  const sortedDates = Array.from(allDatesSet).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  const sortedActivities = [];
  sortedDates.forEach((date) => {
    let dailyItems = groupedItinerary[date] ? [...groupedItinerary[date]] : [];
    dailyItems.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined && a.order !== b.order)
        return a.order - b.order;
      return (
        parseInt((a.time || "00:00").replace(":", "")) -
        parseInt((b.time || "00:00").replace(":", ""))
      );
    });
    const dailyVirtuals = virtualItems
      .filter((v) => v.date === date)
      .sort(
        (a, b) =>
          parseInt((a.time || "00:00").replace(":", "")) -
          parseInt((b.time || "00:00").replace(":", ""))
      );
    dailyVirtuals.forEach((vItem) => {
      const vTime = parseInt((vItem.time || "00:00").replace(":", ""));
      const insertIndex = dailyItems.findIndex(
        (rItem) => parseInt((rItem.time || "00:00").replace(":", "")) > vTime
      );
      if (insertIndex === -1) dailyItems.push(vItem);
      else dailyItems.splice(insertIndex, 0, vItem);
    });
    groupedItinerary[date] = dailyItems;
    dailyItems.forEach((item) => sortedActivities.push(item));
  });

  const renderActivityForm = () => (
    <div style={formBgStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "14px",
          borderBottom: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
          paddingBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "4px",
            background: d ? "#09090b" : "#f4f4f5",
            borderRadius: "10px",
            padding: "3px",
          }}
        >
          {[
            { id: "place", icon: "📍", label: "สถานที่" },
            { id: "todo", icon: "☑️", label: "Todo" },
          ].map((ft) => (
            <button
              key={ft.id}
              type="button"
              onClick={() => setFormType(ft.id)}
              style={{
                padding: "6px 14px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                background:
                  formType === ft.id ? (d ? "#27272a" : "#fff") : "transparent",
                color:
                  formType === ft.id
                    ? d
                      ? "#fafafa"
                      : "#09090b"
                    : d
                    ? "#52525b"
                    : "#a1a1aa",
                fontWeight: formType === ft.id ? "700" : "500",
                fontSize: "13px",
                boxShadow:
                  formType === ft.id ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                fontFamily: "inherit",
              }}
            >
              {ft.icon} {ft.label}
            </button>
          ))}
        </div>
        {editingId && (
          <button
            type="button"
            onClick={() => handleDeleteActivity(editingId)}
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.2)",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#f87171",
              padding: "6px 12px",
            }}
          >
            🗑️ ลบ
          </button>
        )}
      </div>
      <form
        onSubmit={handleSaveActivity}
        style={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        <div style={{ display: "flex", gap: "12px" }}>
          <div style={{ flex: 1.5 }}>
            <label style={labelStyle}>วันที่</label>
            <input
              type="date"
              value={activityDate}
              min={trip.start}
              max={trip.end}
              onChange={(e) => setActivityDate(e.target.value)}
              style={inputStyle}
              required
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>เวลา</label>
            <input
              type="text"
              placeholder="09:00"
              maxLength="5"
              value={activityTime}
              onChange={handleActivityTimeInput}
              style={{ ...inputStyle, textAlign: "center" }}
              required
            />
          </div>
        </div>

        {formType === "place" && (
          <>
            <div>
              <label style={labelStyle}>ชื่อสถานที่</label>
              <input
                type="text"
                placeholder="เช่น วัดเซนโซจิ, Universal Studio"
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                style={{ ...inputStyle, fontWeight: "600", fontSize: "15px" }}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>หมวดหมู่</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                {categoryOptions.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border:
                        category === c.id
                          ? `1px solid #3b82f6`
                          : `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
                      backgroundColor:
                        category === c.id
                          ? d
                            ? "rgba(59,130,246,0.15)"
                            : "#eff6ff"
                          : "transparent",
                      color:
                        category === c.id
                          ? "#3b82f6"
                          : d
                          ? "#71717a"
                          : "#a1a1aa",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "inherit",
                    }}
                  >
                    {c.icon} {c.label}
                  </button>
                ))}

                {isAddingCategory ? (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      placeholder="ชื่อหมวดหมู่..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                      autoFocus
                      style={{
                        ...inputStyle,
                        padding: "6px 10px",
                        fontSize: "12px",
                        width: "110px",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "10px",
                        border: "none",
                        background: "#10b981",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      ✓
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingCategory(false);
                        setNewCategoryName("");
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "10px",
                        border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
                        background: "transparent",
                        color: d ? "#a1a1aa" : "#71717a",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsAddingCategory(true)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: `1px dashed ${d ? "#3f3f46" : "#d4d4d8"}`,
                      backgroundColor: "transparent",
                      color: d ? "#a1a1aa" : "#71717a",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "inherit",
                    }}
                  >
                    + เพิ่ม
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>📍 Map Link</label>
                <input
                  type="text"
                  placeholder="วางลิงก์ที่นี่..."
                  value={googleMapUrl}
                  onChange={(e) => setGoogleMapUrl(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>🔗 Links อื่นๆ</label>
                <input
                  type="text"
                  placeholder="วางลิงก์ที่นี่..."
                  value={otherUrl}
                  onChange={(e) => setOtherUrl(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>บันทึกเพิ่มเติม</label>
              <textarea
                ref={detailTextareaRef}
                placeholder="รายละเอียดเพิ่มเติม..."
                value={activityDetail}
                onChange={(e) => setActivityDetail(e.target.value)}
                rows="2"
                style={{
                  ...inputStyle,
                  resize: "none",
                  fontFamily: "inherit",
                  overflow: "hidden",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>ค่าใช้จ่าย</label>
                <input
                  type="number"
                  placeholder="0"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>สกุลเงิน</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={inputStyle}
                >
                  <option value="THB">🇹🇭 THB</option>
                  <option value="JPY">🇯🇵 JPY</option>
                </select>
              </div>
            </div>
          </>
        )}

        {formType === "todo" && (
          <>
            <div>
              <label style={labelStyle}>หัวข้อ Todo</label>
              <input
                type="text"
                placeholder="เช่น ของที่ต้องเตรียม"
                value={todoTitle}
                onChange={(e) => setTodoTitle(e.target.value)}
                style={{ ...inputStyle, fontWeight: "600", fontSize: "15px" }}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>
                รายการย่อย (เคาะบรรทัดใหม่เพื่อเพิ่ม)
              </label>
              <textarea
                ref={todoTextareaRef}
                placeholder={"รายการที่ 1\nรายการที่ 2..."}
                value={tasksText}
                onChange={(e) => setTasksText(e.target.value)}
                rows="4"
                style={{
                  ...inputStyle,
                  resize: "none",
                  fontFamily: "inherit",
                  overflow: "hidden",
                }}
              />
            </div>
          </>
        )}

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "4px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setInlineAddDate(null);
              setActiveMapQuery(trip.name);
            }}
            style={{
              padding: "8px 18px",
              borderRadius: "10px",
              border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
              background: "transparent",
              color: d ? "#a1a1aa" : "#71717a",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "600",
              fontFamily: "inherit",
            }}
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            style={{
              padding: "8px 22px",
              borderRadius: "10px",
              border: "none",
              background:
                formType === "place"
                  ? "linear-gradient(135deg,#3b82f6,#2563eb)"
                  : "linear-gradient(135deg,#f59e0b,#d97706)",
              color: "#fff",
              fontWeight: "700",
              cursor: "pointer",
              fontSize: "13px",
              fontFamily: "inherit",
            }}
          >
            {editingId ? "💾 บันทึก" : "＋ บันทึกแผน"}
          </button>
        </div>
      </form>
    </div>
  );

  const renderContent = () => {
    if (activeTab === "summary")
      return (
        <SummaryTab
          trip={trip}
          theme={theme}
          lang={printLang}
          onNavigate={setActiveTab}
        />
      );
    if (activeTab === "flight")
      return (
        <FlightTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );
    if (activeTab === "stay")
      return (
        <AccommodationTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );
    if (activeTab === "insurance")
      return (
        <InsuranceTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );
    if (activeTab === "shopping")
      return (
        <ShoppingTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );
    if (activeTab === "expense")
      // <--- เพิ่ม Block นี้
      return (
        <ExpenseTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );

    if (activeTab === "members")
      return (
        <MembersTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );

    if (activeTab === "contacts")
      return (
        <ContactsTab
          trip={trip}
          theme={theme}
          onUpdateTrip={handleUpdateTripData}
          isDarkMode={isDarkMode}
        />
      );

    if (activeTab === "plan") {
      return (
        <div className="plan-split-view">
          <div className="plan-left-pane no-print">
            {!inlineAddDate && !editingId && sortedActivities.length === 0 && (
              <button
                onClick={() => handleOpenAddForm("TOP")}
                style={{
                  padding: "15px",
                  borderRadius: "14px",
                  width: "100%",
                  marginBottom: "16px",
                  border: `2px dashed ${d ? "#27272a" : "#e4e4e7"}`,
                  background: "transparent",
                  color: d ? "#52525b" : "#a1a1aa",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#3b82f6";
                  e.currentTarget.style.color = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = d ? "#27272a" : "#e4e4e7";
                  e.currentTarget.style.color = d ? "#52525b" : "#a1a1aa";
                }}
              >
                ➕ เริ่มสร้างแผนแรกของคุณ
              </button>
            )}

            {inlineAddDate === "TOP" && renderActivityForm()}

            <div style={{ paddingBottom: "20px" }}>
              <DragDropContext onDragEnd={onDragEnd}>
                {sortedDates.map((date) => {
                  let dailyPlaceCounter = 0;
                  return (
                    <div key={date}>
                      <div
                        style={{
                          margin: "24px 0 12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: `1px solid ${
                            d ? "#27272a" : "#e4e4e7"
                          }`,
                          paddingBottom: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: theme.text,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          📅{" "}
                          {new Date(date).toLocaleDateString("th-TH", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: d ? "#52525b" : "#a1a1aa",
                            fontWeight: "500",
                          }}
                        >
                          {date}
                        </span>
                      </div>

                      <Droppable droppableId={date}>
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            {groupedItinerary[date].map((item, index) => {
                              const isActive =
                                activeMapQuery ===
                                (item.mapQuery || item.place || item.name);
                              const isEditingThis =
                                editingId === item.id && !item.isVirtual;
                              if (item.type !== "todo" && !item.isVirtual)
                                dailyPlaceCounter++;

                              return (
                                <Draggable
                                  key={String(item.id)}
                                  draggableId={String(item.id)}
                                  index={index}
                                  isDragDisabled={item.isVirtual}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {isEditingThis ? (
                                        renderActivityForm()
                                      ) : (
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "10px",
                                            opacity: item.isVirtual
                                              ? 0.9
                                              : snapshot.isDragging
                                              ? 0.85
                                              : 1,
                                            background: isActive
                                              ? item.type === "todo"
                                                ? "rgba(245,158,11,0.08)"
                                                : "rgba(59,130,246,0.08)"
                                              : d
                                              ? "#18181b"
                                              : "#fff",
                                            borderRadius: "12px",
                                            padding: "12px 80px 12px 14px",
                                            border: `1px solid ${
                                              isActive
                                                ? item.type === "todo"
                                                  ? "rgba(245,158,11,0.4)"
                                                  : "rgba(59,130,246,0.4)"
                                                : snapshot.isDragging
                                                ? "#3b82f6"
                                                : d
                                                ? "#27272a"
                                                : "#e4e4e7"
                                            }`,
                                            borderLeft: item.isVirtual
                                              ? `3px solid ${
                                                  item.type === "flight"
                                                    ? "#10b981"
                                                    : "#0ea5e9"
                                                }`
                                              : item.type === "todo" &&
                                                !isActive
                                              ? `3px solid #f59e0b`
                                              : undefined,
                                            cursor: "pointer",
                                            position: "relative",
                                            transition: "all 0.15s",
                                            boxShadow: snapshot.isDragging
                                              ? "0 12px 30px rgba(0,0,0,0.2)"
                                              : "none",
                                          }}
                                          onClick={() =>
                                            !item.isVirtual &&
                                            setActiveMapQuery(
                                              item.mapQuery ||
                                                item.place ||
                                                item.name
                                            )
                                          }
                                        >
                                          <div
                                            {...provided.dragHandleProps}
                                            style={{
                                              color: item.isVirtual
                                                ? "transparent"
                                                : d
                                                ? "#3f3f46"
                                                : "#d4d4d8",
                                              fontSize: "16px",
                                              display: "flex",
                                              alignItems: "center",
                                              cursor: item.isVirtual
                                                ? "default"
                                                : "grab",
                                              flexShrink: 0,
                                              userSelect: "none",
                                            }}
                                          >
                                            ⠿
                                          </div>

                                          {!item.isVirtual &&
                                            item.type !== "todo" && (
                                              <div
                                                className="place-number"
                                                style={{ flexShrink: 0 }}
                                              >
                                                {dailyPlaceCounter}
                                              </div>
                                            )}

                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                gap: "8px",
                                              }}
                                            >
                                              <div style={{ minWidth: 0 }}>
                                                <span
                                                  style={{
                                                    fontSize: "13px",
                                                    fontWeight: "700",
                                                    color: item.isVirtual
                                                      ? item.type === "flight"
                                                        ? "#10b981"
                                                        : "#0ea5e9"
                                                      : theme.text,
                                                    display: "block",
                                                    lineHeight: "1.35",
                                                  }}
                                                >
                                                  {item.name}
                                                </span>
                                                {item.place && (
                                                  <span
                                                    style={{
                                                      fontSize: "12px",
                                                      color: d
                                                        ? "#71717a"
                                                        : "#a1a1aa",
                                                      display: "block",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    📍 {item.place}
                                                  </span>
                                                )}
                                              </div>
                                              <span
                                                style={{
                                                  fontSize: "11px",
                                                  fontWeight: "600",
                                                  flexShrink: 0,
                                                  color: d
                                                    ? "#52525b"
                                                    : "#a1a1aa",
                                                  background: d
                                                    ? "#27272a"
                                                    : "#f4f4f5",
                                                  padding: "2px 7px",
                                                  borderRadius: "999px",
                                                }}
                                              >
                                                {item.time}
                                              </span>
                                            </div>

                                            {item.isVirtual &&
                                              item.type === "flight" &&
                                              editingCityFlightId ===
                                                item.originalId && (
                                                <div
                                                  style={{
                                                    marginTop: "8px",
                                                    display: "flex",
                                                    gap: "6px",
                                                    alignItems: "center",
                                                  }}
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <input
                                                    value={editCityDep}
                                                    onChange={(e) =>
                                                      setEditCityDep(
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder="ต้นทาง"
                                                    style={{
                                                      ...inputStyle,
                                                      flex: 1,
                                                      padding: "5px 8px",
                                                      fontSize: "12px",
                                                    }}
                                                  />
                                                  <span
                                                    style={{
                                                      color: d
                                                        ? "#52525b"
                                                        : "#a1a1aa",
                                                    }}
                                                  >
                                                    →
                                                  </span>
                                                  <input
                                                    value={editCityArr}
                                                    onChange={(e) =>
                                                      setEditCityArr(
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder="ปลายทาง"
                                                    style={{
                                                      ...inputStyle,
                                                      flex: 1,
                                                      padding: "5px 8px",
                                                      fontSize: "12px",
                                                    }}
                                                  />
                                                  <button
                                                    onClick={() =>
                                                      handleSaveFlightCity(
                                                        item.originalId
                                                      )
                                                    }
                                                    style={{
                                                      padding: "5px 10px",
                                                      borderRadius: "7px",
                                                      border: "none",
                                                      background: "#10b981",
                                                      color: "#fff",
                                                      cursor: "pointer",
                                                      fontSize: "12px",
                                                    }}
                                                  >
                                                    ✓
                                                  </button>
                                                  <button
                                                    onClick={() =>
                                                      setEditingCityFlightId(
                                                        null
                                                      )
                                                    }
                                                    style={{
                                                      padding: "5px 8px",
                                                      borderRadius: "7px",
                                                      border: `1px solid ${
                                                        d
                                                          ? "#27272a"
                                                          : "#e4e4e7"
                                                      }`,
                                                      background: "transparent",
                                                      color: d
                                                        ? "#a1a1aa"
                                                        : "#71717a",
                                                      cursor: "pointer",
                                                      fontSize: "12px",
                                                    }}
                                                  >
                                                    ✕
                                                  </button>
                                                </div>
                                              )}

                                            {item.detail &&
                                              !editingCityFlightId && (
                                                <div
                                                  style={{
                                                    fontSize: "12px",
                                                    color: d
                                                      ? "#71717a"
                                                      : "#a1a1aa",
                                                    marginTop: "4px",
                                                    whiteSpace: "pre-wrap",
                                                    lineHeight: "1.5",
                                                  }}
                                                >
                                                  {item.detail}
                                                </div>
                                              )}

                                            {item.checkInTime && (
                                              <div
                                                style={{
                                                  marginTop: "5px",
                                                  fontSize: "11px",
                                                  color: "#f59e0b",
                                                  fontWeight: "600",
                                                }}
                                              >
                                                ⏰ แนะนำเช็คอิน:{" "}
                                                {item.checkInTime}
                                              </div>
                                            )}

                                            {(item.inputCost > 0 ||
                                              item.googleMapUrl ||
                                              item.otherUrl) && (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: "6px",
                                                  marginTop: "8px",
                                                  alignItems: "center",
                                                }}
                                              >
                                                {item.inputCost > 0 && (
                                                  <span
                                                    style={{
                                                      fontSize: "11px",
                                                      fontWeight: "700",
                                                      background:
                                                        "rgba(16,185,129,0.1)",
                                                      color: "#10b981",
                                                      border:
                                                        "1px solid rgba(16,185,129,0.25)",
                                                      padding: "2px 8px",
                                                      borderRadius: "999px",
                                                    }}
                                                  >
                                                    {item.currency === "JPY"
                                                      ? "¥"
                                                      : "฿"}
                                                    {item.inputCost?.toLocaleString()}
                                                  </span>
                                                )}
                                                {item.googleMapUrl && (
                                                  <a
                                                    href={item.googleMapUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    style={{
                                                      fontSize: "11px",
                                                      color: "#3b82f6",
                                                      fontWeight: "600",
                                                      textDecoration: "none",
                                                    }}
                                                  >
                                                    🗺️ Map
                                                  </a>
                                                )}
                                                {item.otherUrl && (
                                                  <a
                                                    href={item.otherUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    style={{
                                                      fontSize: "11px",
                                                      color: "#8b5cf6",
                                                      fontWeight: "600",
                                                      textDecoration: "none",
                                                    }}
                                                  >
                                                    🔗 Link
                                                  </a>
                                                )}
                                              </div>
                                            )}

                                            {item.type === "todo" &&
                                              item.tasks?.length > 0 && (
                                                <div
                                                  style={{ marginTop: "8px" }}
                                                >
                                                  {item.tasks.map((task) => (
                                                    <div
                                                      key={task.id}
                                                      className="todo-item"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleTodo(
                                                          item.id,
                                                          task.id
                                                        );
                                                      }}
                                                    >
                                                      <input
                                                        type="checkbox"
                                                        checked={
                                                          task.isCompleted
                                                        }
                                                        onChange={() => {}}
                                                        style={{
                                                          accentColor:
                                                            "#f59e0b",
                                                          width: "15px",
                                                          height: "15px",
                                                          flexShrink: 0,
                                                        }}
                                                      />
                                                      <label
                                                        className={
                                                          task.isCompleted
                                                            ? "completed"
                                                            : ""
                                                        }
                                                        style={{
                                                          fontSize: "13px",
                                                          cursor: "pointer",
                                                        }}
                                                      >
                                                        {task.text}
                                                      </label>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                          </div>

                                          {!item.isVirtual && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditClick(item);
                                              }}
                                              style={{
                                                position: "absolute",
                                                top: "8px",
                                                right: "8px",
                                                background: "none",
                                                border: "none",
                                                color: d
                                                  ? "#a1a1aa"
                                                  : "#71717a",
                                                fontSize: "20px",
                                                cursor: "pointer",
                                                padding: "10px",
                                                borderRadius: "6px",
                                                lineHeight: 1,
                                                zIndex: 10,
                                              }}
                                            >
                                              ⋮
                                            </button>
                                          )}

                                          {item.isVirtual && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.type === "flight") {
                                                  const origF =
                                                    trip.flightList.find(
                                                      (f) =>
                                                        f.id === item.originalId
                                                    );
                                                  setEditCityDep(
                                                    origF?.depCity || ""
                                                  );
                                                  setEditCityArr(
                                                    origF?.arrCity || ""
                                                  );
                                                  setEditingCityFlightId(
                                                    item.originalId
                                                  );
                                                } else {
                                                  setActiveTab(item.sourceTab);
                                                }
                                              }}
                                              style={{
                                                position: "absolute",
                                                top: "10px",
                                                right: "10px",
                                                background:
                                                  item.type === "flight"
                                                    ? "rgba(16,185,129,0.1)"
                                                    : "rgba(14,165,233,0.1)",
                                                border: `1px solid ${
                                                  item.type === "flight"
                                                    ? "rgba(16,185,129,0.3)"
                                                    : "rgba(14,165,233,0.3)"
                                                }`,
                                                color:
                                                  item.type === "flight"
                                                    ? "#10b981"
                                                    : "#0ea5e9",
                                                borderRadius: "7px",
                                                padding: "6px 10px",
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                cursor: "pointer",
                                                zIndex: 10,
                                              }}
                                            >
                                              {item.type === "flight"
                                                ? "✏️ เมือง"
                                                : "→ แก้ไข"}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}

                            {inlineAddDate === date
                              ? renderActivityForm()
                              : !editingId && (
                                  <div
                                    onClick={() => handleOpenAddForm(date)}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "8px 0",
                                      cursor: "pointer",
                                      opacity: 0.4,
                                      transition: "opacity 0.2s",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.opacity = 1)
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.opacity = 0.4)
                                    }
                                  >
                                    <div
                                      style={{
                                        flex: 1,
                                        height: "1px",
                                        borderTop: `2px dashed ${
                                          d ? "#27272a" : "#e4e4e7"
                                        }`,
                                      }}
                                    />
                                    <div
                                      style={{
                                        background: d ? "#27272a" : "#f4f4f5",
                                        color: d ? "#a1a1aa" : "#71717a",
                                        padding: "4px 14px",
                                        borderRadius: "999px",
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        margin: "0 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      ＋ เพิ่มแผนในวันนี้
                                    </div>
                                    <div
                                      style={{
                                        flex: 1,
                                        height: "1px",
                                        borderTop: `2px dashed ${
                                          d ? "#27272a" : "#e4e4e7"
                                        }`,
                                      }}
                                    />
                                  </div>
                                )}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </DragDropContext>
            </div>
          </div>

          <div
            className="plan-right-pane no-print"
            style={{
              border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            <iframe
              title="Map"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src={`https://maps.google.com/maps?q=${encodeURIComponent(
                activeMapQuery
              )}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
            />
            <div
              style={{
                position: "absolute",
                top: "14px",
                left: "14px",
                background: d ? "rgba(24,24,27,0.9)" : "rgba(255,255,255,0.92)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                color: theme.text,
                padding: "6px 14px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                pointerEvents: "none",
                border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
              }}
            >
              📍 {activeMapQuery}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const nights =
    trip.start && trip.end
      ? Math.max(
          0,
          Math.round((new Date(trip.end) - new Date(trip.start)) / 86400000)
        )
      : 0;

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <div
        className="no-print"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: d ? "rgba(9,9,11,0.93)" : "rgba(244,244,245,0.93)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
          padding: "0 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            height: "60px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minWidth: 0,
            }}
          >
            <button
              onClick={onBack}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "34px",
                height: "34px",
                borderRadius: "10px",
                flexShrink: 0,
                border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
                background: d ? "#18181b" : "#fff",
                color: theme.text,
                cursor: "pointer",
                fontSize: "16px",
              }}
              title="กลับ"
            >
              ←
            </button>
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "700",
                  letterSpacing: "-0.02em",
                  color: theme.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "280px",
                }}
              >
                {trip.name}
              </h2>
              {trip.start && trip.end && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "11px",
                    color: d ? "#71717a" : "#a1a1aa",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {trip.start} → {trip.end}
                  {nights > 0 && (
                    <span
                      style={{
                        background: d ? "#27272a" : "#f4f4f5",
                        border: `1px solid ${d ? "#3f3f46" : "#e4e4e7"}`,
                        borderRadius: "999px",
                        padding: "1px 7px",
                        fontSize: "10px",
                        fontWeight: "600",
                      }}
                    >
                      {nights} คืน
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            {[
              {
                label: `📄 ${printLang.toUpperCase()}`,
                title: "เปลี่ยนภาษา PDF",
                onClick: () => setPrintLang(printLang === "th" ? "en" : "th"),
              },
              { label: "💾", title: "Backup JSON", onClick: handleExportJSON },
              { label: "🖨️", title: "Print", onClick: () => window.print() },
            ].map((btn, i) => (
              <button
                key={i}
                onClick={btn.onClick}
                title={btn.title}
                style={{
                  height: "34px",
                  padding: "0 12px",
                  borderRadius: "10px",
                  border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
                  background: d ? "#18181b" : "#fff",
                  color: theme.text,
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="hide-scrollbar"
          style={{ display: "flex", gap: "2px", overflowX: "auto" }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "10px 14px",
                  background: "transparent",
                  border: "none",
                  borderBottom: isActive
                    ? `2px solid ${tab.color}`
                    : "2px solid transparent",
                  color: isActive ? tab.color : d ? "#71717a" : "#a1a1aa",
                  fontWeight: isActive ? "700" : "500",
                  fontSize: "13px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = theme.text;
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    e.currentTarget.style.color = d ? "#71717a" : "#a1a1aa";
                }}
              >
                <span style={{ fontSize: "14px" }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="no-print" style={{ flex: 1, padding: "20px 24px" }}>
        {renderContent()}
      </div>

      {/* ══ PRINT ONLY ══════════════════════════════════════════ */}
      <div className="print-only">
        <h1
          style={{
            textAlign: "center",
            borderBottom: "2px solid #000",
            paddingBottom: "10px",
          }}
        >
          🇯🇵 {t.docTitle}: {trip.name}
        </h1>
        <p style={{ textAlign: "center" }}>
          {t.period}: {trip.start} {t.to} {trip.end}
        </p>
        <SummaryTab
          trip={trip}
          theme={{ cardBg: "white", border: "#ccc", text: "black" }}
          lang={printLang}
        />
        <div className="page-break" />
        <h3>📝 {t.itinerarySection}</h3>
        {sortedActivities.map((item, index) => {
          const prevItem = sortedActivities[index - 1];
          const showDateHeader = !prevItem || prevItem.date !== item.date;
          return (
            <React.Fragment key={`print_${item.id}`}>
              {showDateHeader && (
                <h4
                  style={{
                    borderBottom: "1px solid #000",
                    paddingBottom: "5px",
                    marginTop: "20px",
                  }}
                >
                  📅 {t.dateTitle}: {item.date}
                </h4>
              )}
              <div
                style={{
                  borderBottom: "1px solid #eee",
                  padding: "10px 0",
                  display: "flex",
                  gap: "20px",
                }}
              >
                <div style={{ minWidth: "100px" }}>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: item.isVirtual ? "#0984e3" : "#000",
                    }}
                  >
                    {item.time}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <strong
                    style={{
                      fontSize: "16px",
                      color: item.isVirtual ? "#0984e3" : "#000",
                    }}
                  >
                    {item.type === "todo" ? `📋 ${item.name}` : item.name}
                  </strong>
                  {item.place && (
                    <div style={{ fontSize: "13px", color: "#555" }}>
                      📍 {item.place}
                    </div>
                  )}
                  {item.detail && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#333",
                        marginTop: "4px",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {item.detail}
                    </div>
                  )}

                  {(item.googleMapUrl || item.otherUrl || item.guideLink) && (
                    <div
                      style={{ display: "flex", gap: "15px", marginTop: "8px" }}
                    >
                      {item.googleMapUrl && (
                        <PrintQRCode url={item.googleMapUrl} label="Map" />
                      )}
                      {item.otherUrl && (
                        <PrintQRCode url={item.otherUrl} label="Link" />
                      )}
                      {item.guideLink && (
                        <PrintQRCode url={item.guideLink} label="Guide" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}

        <div className="page-break" />
        <h3>✈️ {t.flightSection}</h3>
        <table className="compare-table">
          <thead>
            <tr>
              <th>{t.colDate}</th>
              <th>{t.colFlight}</th>
              <th>{t.colRoute}</th>
              <th>{t.colTime}</th>
              <th>QR</th>
            </tr>
          </thead>
          <tbody>
            {trip.flightList?.map((f) => {
              const flightDate = f.depDate
                ? new Date(f.depDate).toLocaleDateString("th-TH", {
                    day: "numeric",
                    month: "short",
                  })
                : "-";
              const flightName =
                `${f.airline || ""} ${f.flightNo || ""}`.trim() || "-";
              return (
                <tr key={f.id}>
                  <td>{flightDate}</td>
                  <td>{flightName}</td>
                  <td>
                    {f.depCode || f.depCity || "ไม่ระบุ"} ➝{" "}
                    {f.arrCode || f.arrCity || "ไม่ระบุ"}
                  </td>
                  <td>
                    {f.depTime || "00:00"} - {f.arrTime || "00:00"}
                  </td>
                  <td>
                    {f.ticketLink && (
                      <PrintQRCode url={f.ticketLink} label="Ticket" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="page-break" />
        <h3>🏨 {t.staySection}</h3>
        <table className="compare-table">
          <thead>
            <tr>
              <th>{t.colHotel}</th>
              <th>{t.colCheckInOut}</th>
              <th>{t.colPrice}</th>
              <th>{t.colTotal}</th>
              <th>QR</th>
            </tr>
          </thead>
          <tbody>
            {trip.accommodations?.map((a) => (
              <tr key={a.id}>
                <td>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      marginBottom: "6px",
                    }}
                  >
                    {a.name}
                  </div>

                  {/* 🌟 แสดงข้อมูล Agency, เบอร์โทร, อีเมล ตรงนี้ 🌟 */}
                  {a.agency && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "2px",
                      }}
                    >
                      🏢 จองผ่าน: {a.agency}
                    </div>
                  )}
                  {a.phone && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "2px",
                      }}
                    >
                      📞 โทร: {a.phone}
                    </div>
                  )}
                  {a.email && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "2px",
                      }}
                    >
                      📧 อีเมล: {a.email}
                    </div>
                  )}

                  {a.accessCode && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "12px",
                        color: "#d97706",
                        fontWeight: "bold",
                      }}
                    >
                      🔑 รหัสเข้าพัก: {a.accessCode}
                    </div>
                  )}
                </td>
                <td>
                  ✅ {a.checkInDate || "-"} {a.checkInTime || "14:00"}
                  <br />
                  🔑 {a.checkOutDate || "-"} {a.checkOutTime || "11:00"}
                </td>
                <td>฿{(a.price || 0).toLocaleString()}</td>
                <td>฿{(a.totalPrice || 0).toLocaleString()}</td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    {a.googleMap && (
                      <PrintQRCode url={a.googleMap} label="Map" />
                    )}
                    {a.link && <PrintQRCode url={a.link} label="Booking" />}
                    {a.checkInLink && (
                      <PrintQRCode url={a.checkInLink} label="Check-in" />
                    )}
                    {a.guideLink && (
                      <PrintQRCode url={a.guideLink} label="Guide" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="page-break" />
        <h3>🛡️ {t.insuranceSection}</h3>
        <table className="compare-table">
          <thead>
            <tr>
              <th>{t.colInsName}</th>
              <th>{t.colPolicy}</th>
              <th>{t.colContact}</th>
              <th>QR</th>
            </tr>
          </thead>
          <tbody>
            {trip.insurances?.map((ins) => (
              <tr key={ins.id}>
                <td>
                  <strong>{ins.company}</strong>
                  <br />
                  {ins.planName}
                </td>
                <td>{ins.policyNo || "-"}</td>
                <td>{ins.emergencyContact || "-"}</td>
                <td>
                  {ins.link && <PrintQRCode url={ins.link} label="กรมธรรม์" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {trip.members && trip.members.length > 0 && (
          <>
            <div className="page-break" />
            <h3>👥 {t.membersSection}</h3>
            <table className="compare-table">
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>{t.colName}</th>
                  <th style={{ width: "25%" }}>{t.colContactDetail}</th>
                  <th style={{ width: "25%" }}>{t.colEmergency}</th>
                  <th style={{ width: "25%" }}>{t.colAllergies}</th>
                </tr>
              </thead>
              <tbody>
                {trip.members.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.thaiName}</strong>
                      <br />
                      <span style={{ fontSize: "12px", color: "#555" }}>
                        {m.engName}
                      </span>
                    </td>
                    <td style={{ fontSize: "12px", lineHeight: "1.5" }}>
                      {m.phone && <div>📞 Phone : {m.phone}</div>}
                      {m.lineId && <div>💬 Line : {m.lineId}</div>}
                      {m.email && <div>📧 Email :{m.email}</div>}
                      {!m.phone && !m.lineId && !m.email && "-"}
                    </td>
                    <td
                      style={{
                        fontSize: "12px",
                        color: "#d32f2f",
                        fontWeight: "bold",
                      }}
                    >
                      {m.emergencyContact || "-"}
                    </td>
                    <td style={{ fontSize: "12px" }}>
                      {m.allergies && m.allergies.length > 0 ? (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: "15px",
                            color: "#e67e22",
                          }}
                        >
                          {m.allergies.map((allergy, idx) => (
                            <li key={idx}>{allergy}</li>
                          ))}
                        </ul>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {trip.importantContacts && trip.importantContacts.length > 0 && (
          <>
            <div className="page-break" />
            <h3 style={{ color: "#e74c3c" }}>📞 {t.contactsSection}</h3>
            <table className="compare-table">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>{t.colContactTitle}</th>
                  <th style={{ width: "50%" }}>{t.colContactInfo}</th>
                  <th style={{ width: "20%" }}>QR Link</th>
                </tr>
              </thead>
              <tbody>
                {trip.importantContacts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.title}</strong>
                      <br />
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          textTransform: "uppercase",
                        }}
                      >
                        {c.category}
                      </span>
                    </td>
                    <td style={{ fontSize: "12px", lineHeight: "1.6" }}>
                      {c.phone && <div>📞 {c.phone}</div>}
                      {c.email && <div>📧 {c.email}</div>}
                      {c.locationName && <div>📍 {c.locationName}</div>}
                      {c.note && <div>📝 {c.note}</div>}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {c.mapLink && (
                          <PrintQRCode url={c.mapLink} label="Map" />
                        )}
                        {c.otherLink && (
                          <PrintQRCode url={c.otherLink} label="Link" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default TripDetail;
